import { Injectable } from "@angular/core";
import * as DeviceDetector from "device-detector-js";
import { Helper } from "src/app/core/helpers/helper";
import { LanguageService } from "src/app/core/services/language.service";
import { AuthService } from "./auth.service";
import { VehicleService } from "./vehicle.service";
import { Observable } from "rxjs";
import { apiColletions } from "src/app/core/constants/api_collection";
import { ApiService } from "./api.service";

@Injectable({
  providedIn: "root"
})
export class CommonService {
  public accessibilityList: Array<any> = [];
  public vehicleTypesList: Array<any> = [];

  constructor(
    private authService: AuthService,
    private languageService: LanguageService,
    private helper: Helper,
    private vehicleService: VehicleService,
    private apiService: ApiService
  ) {}

  getLoginToken(loginToken: any = null): Promise<any> {
    return new Promise((resolve) => {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const packageJson = require("../../../../package.json");
      const device = this.getDeviceInfo();

      const { manufacturer, deviceName } = this.getManufacturerAndDeviceName(device);

      const uuid = loginToken ?? this.setUuid();

      const parameters = {
        deviceId: uuid,
        deviceType: this.helper.DeviceTypeEnum.WEB,
        manufacturer: manufacturer,
        deviceName: deviceName,
        os: device.os?.name?.toString() ?? "",
        appVersion: packageJson.version,
        isShowSuccessToast: false
      };

      this.authService.getToken(parameters).subscribe((response: any) => {
        if (response) {
          this.helper.getLoginToken.next(response.token);
        }
        resolve(true);
      });
    });
  }

  getDeviceInfo(): any {
    const userAgent = window.navigator.userAgent;
    const deviceDetector = new DeviceDetector();
    return deviceDetector.parse(userAgent);
  }

  getManufacturerAndDeviceName(device: any): any {
    let manufacturer = device.device?.brand?.toString() ?? "";
    let deviceName = device.device?.model?.toString() ?? "";

    if (!manufacturer || !deviceName) {
      manufacturer = this.getManufacturer(device.os?.name);
      deviceName = this.getDeviceName(device.os?.name, device.os?.version);
    }

    return { manufacturer, deviceName };
  }

  getManufacturer(osName: string): string {
    switch (osName) {
      case "Windows":
        return "Microsoft";
      case "iOS":
      case "Mac":
      case "Apple":
        return "Apple";
      default:
        return osName?.toString() ?? "";
    }
  }

  getDeviceName(osName: string, osVersion: string): string {
    switch (osName) {
      case "Windows":
        return `${osName?.toString()} ${osVersion?.toString()}`;
      case "iOS":
      case "Mac":
      case "Apple":
        return "Apple";
      default:
        return osName?.toString() ?? "";
    }
  }

  setUuid(): string {
    const uuid = this.helper.uuid;
    localStorage.setItem("loginDeviceToken", uuid);
    return uuid;
  }

  mapVehicleTypeIdsToNames(cityIds: string[]): Promise<string[]> {
    return new Promise<string[]>((resolve) => {
      const typeNames: string[] = [];
      this.vehicleTypesList.forEach((type: any) => {
        if (cityIds.includes(type._id)) {
          typeNames.push(type.name[this.helper.cuurentLang]);
        }
      });

      resolve(typeNames);
    });
  }

  loadScripts(): Promise<any> {
    return new Promise((resolve) => {
      this.authService.loadScripts().then(() => {
        resolve(true);
      });
    });
  }

  mapAccessibilityIdsToNames(accessibilityIds: string[]): Promise<string[]> {
    return new Promise<string[]>((resolve) => {
      const accessibilityNames: string[] = [];
      this.accessibilityList.forEach((data: any) => {
        if (accessibilityIds.includes(data._id)) {
          accessibilityNames.push(data.accessibility);
        }
      });

      resolve(accessibilityNames);
    });
  }

  getAccessibilityList(): Promise<any> {
    return new Promise((resolve) => {
      this.accessibilityList = []; // Initialize the accessibilityList array
      const parameters: any = {}; // Define parameters for the request
      this.vehicleService.getAccessibilityList(parameters).subscribe((response: any) => {
        // Make a request to fetch the list of countries
        if (response?.accessibilities) {
          this.accessibilityList = response.accessibilities; // Populate the accessibilityList array with the fetched countries
          resolve(this.accessibilityList);
        } else {
          this.accessibilityList = [];
          resolve(this.accessibilityList);
        }
      });
    });
  }

  getExportHistoryList(historyType: any, parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const url: any = `${apiColletions.exportHistory}/list` + `/${historyType}`;
      this.apiService.get({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  deleteExportHistory(exportHistoryId: string, parameters: any = {}): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const url = `${apiColletions.exportHistory}/${exportHistoryId}`;
      this.apiService.delete({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  getCancellationReasonList(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.get({ url: apiColletions.cancellationReason, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  getCityList(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.get({ url: apiColletions.city, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  sendMassNotification(massNotificationType: number, parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      const notifiationsUrl: any = `${apiColletions.notifiations}/${massNotificationType}`;
      this.apiService.post({ url: notifiationsUrl, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next({ data: response.data, id: response.id });
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  getAgencies(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.get({ url: apiColletions.agency, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }
}
